import { Select } from "antd";
import { StudyYearEnum } from "../../Api/models/StudyYearEnum";
import { AccreditationTypeEnum, SemesterTypeEnum, StudyPlanDTO, StudyPlanStatusEnum, StudyProgramLangEnum, SubjectStatusEnum } from "../../Api";
import { useIsAdmin, useIsDeanOfFaculty, useIsDepartmentDirector, useIsLabTitular, useIsTitular, useSubject } from "../../utils/utilFunctions";

const { Option } = Select;

export const mapStudyProgramLangToString = (lang: StudyProgramLangEnum): string => {
  switch (lang) {
    case StudyProgramLangEnum.Romana:
      return 'Română';
    case StudyProgramLangEnum.Engleza:
      return 'Engleză';
    case StudyProgramLangEnum.Franceza:
      return 'Franceză';
    case StudyProgramLangEnum.Germana:
      return 'Germană';
    case StudyProgramLangEnum.RoEn:
      return 'Română - Engleză';
    case StudyProgramLangEnum.EnFr:
      return 'Engleză - Franceză';
    default:
      return lang; // fallback
  }
}

export const mapAccreditationTypeToString = (lang: AccreditationTypeEnum): string => {
  switch (lang) {
    case AccreditationTypeEnum.A:
      return 'A';
    case AccreditationTypeEnum.Ap:
      return 'AP';
    case AccreditationTypeEnum.Infiintare:
      return 'În curs de înființare';
    case AccreditationTypeEnum.Lichidare:
      return 'În curs de lichidare';
    default:
      return lang; // fallback
  }
}

export const mapAccreditationTypeShortToString = (lang: AccreditationTypeEnum): string => {
  switch (lang) {
    case AccreditationTypeEnum.A:
      return 'A';
    case AccreditationTypeEnum.Ap:
      return 'AP';
    case AccreditationTypeEnum.Infiintare:
      return 'Înființare';
    case AccreditationTypeEnum.Lichidare:
      return 'Lichidare';
    default:
      return lang; // fallback
  }
}
export const getSchoolYear = (currentDate: Date): number => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    if (currentMonth < 8) {
        return currentYear - 1;
    } else {
        return currentYear;
    }
};

export const getSchoolYearRange = (yearsBack: number): number[] => {
    const currentDate = new Date();
    const nextSchoolYear = getSchoolYear(currentDate);

    return Array.from({ length: yearsBack }, (_, index) => nextSchoolYear - index);
};


export const planOptions = (plans: StudyPlanDTO[], hideCurrentPlan: boolean = false) => {
    const currentPlanOptions = hideCurrentPlan ? [] : getSchoolYearRange(1).map(key => (
        <Option key={key} value={key.toString()}>{'Plan curent'}</Option>
    ));

    return currentPlanOptions.concat(plans.map(plan => (
        <Option key={'cadru ' + plan.planYear} value={'cadru ' + plan.planYear?.toString()}>{'Plan cadru ' + plan.planYear + '-' + plan.endPlanYear}</Option>
    )));
};

export const getStudyYearOptions = (studyCycle: string) => {
    const filteredKeys = Object.keys(StudyYearEnum).filter(key => {
        if (studyCycle === 'Doctorate') {
            return key === 'D1';
        }
        if (studyCycle === 'Master') {
            return key === 'M1' || key === 'M2';
        }
        return key === 'L1' || key === 'L2' || key === 'L3' || key === 'L4';
    });

    return filteredKeys.map(key => (
        <Option key={key} value={key}>
            {StudyYearEnum[key as keyof typeof StudyYearEnum]} {/* Use enum value */}
        </Option>
    ));
};

export const semesterOptions = Object.keys(SemesterTypeEnum).filter(key => key !== 'All').map(key => (
    <Option key={key} value={key}>{key}</Option>
));

export const parseSubjectStatusValidationErrors = async (err: any): Promise<string[] | null> => {
    try {
        const body = await err.clone().json();
        if (body?.errors?.length) {
            return body.errors as string[];
        }
    } catch {
        // not a structured validation error
    }
    return null;
};

export const useBlockedByStatus = (subjectId: string) => {

    const subject = useSubject(subjectId);
    const isDean = useIsDeanOfFaculty(subject?.studyProgram?.facultyId);
    const isAdmin = useIsAdmin();
    // Director al departamentului fisei SAU al planului ei (ca SubjectController / IsDepartmentDirectorOfSubject).
    // Hook-urile se apeleaza neconditionat; id-urile goale se filtreaza dupa.
    const isSubjectDepartmentDirector = useIsDepartmentDirector(subject?.departmentId!);
    const isPlanDepartmentDirector = useIsDepartmentDirector(subject?.planDepartmentId!);
    const isDepartmentDirector = (!!subject?.departmentId && !!isSubjectDepartmentDirector)
        || (!!subject?.planDepartmentId && !!isPlanDepartmentDirector);
    const isCourseTitular = useIsTitular(subjectId);
    const isLabTitular = useIsLabTitular(subjectId);
    // Disciplines with 0 configured lecture hours have no course coordinator role in practice,
    // so the lab coordinator is treated as the course titular for these actions.
    const isTitular = isCourseTitular || (subject?.courseWeeklyHours === 0 && isLabTitular);

    // if (subject) {
    //     switch (subject.planStatus) {
    //         case StudyPlanStatusEnum.Draft:
    //             return !(isDean || isDepartmentDirector || isTitular || isAdmin);
    //         case StudyPlanStatusEnum.PendingDeanValidation:
    //             return !(isDean || isAdmin);
    //         case StudyPlanStatusEnum.PendingQaValidation:
    //             return !(isAdmin);
    //         case StudyPlanStatusEnum.Approved:
    //             return !(isAdmin);
    //         default:
    //             return true;
    //     }
    // }

    if (subject) {
        switch (subject.status) {
            case SubjectStatusEnum.Draft:
                return !(isDean || isDepartmentDirector || isTitular || isAdmin);
            case SubjectStatusEnum.PendingDeanValidation:
                return !(isDean || isAdmin);
            case SubjectStatusEnum.PendingQaValidation:
                return !(isAdmin);
            case SubjectStatusEnum.Approved:
                return !(isAdmin);
            default:
                return true;
        }
    }
};